import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Search, X, Check, AlertTriangle, Sparkles, Edit3, Filter } from "lucide-react";
import { useProductShapes } from "../context/ProductShapeContext"; // ← import context

/* ── helper: normalise API status → boolean ── */
const isActive = (item) => item.status === "Active";

/* ─────────────────────────────────────────────
   TOAST
───────────────────────────────────────────── */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const show = useCallback((msg, type = "success") => {
    const id = Date.now();
    setToasts((p) => [...p, { id, msg, type }]);
    setTimeout(() => setToasts((p) => p.filter((t) => t.id !== id)), 3000);
  }, []);
  return { toasts, show };
}

function ToastContainer({ toasts }) {
  return (
    <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
      {toasts.map((t) => (
        <div key={t.id}
          className={`flex items-center gap-2.5 px-4 py-3 rounded-xl shadow-lg text-sm font-medium border animate-slide-up
            ${t.type === "success" ? "bg-[#f0faf4] border-[#bbf7d0] text-[#1a6b3c]"
              : t.type === "warning" ? "bg-amber-50 border-amber-200 text-amber-700"
              : "bg-red-50 border-red-200 text-red-700"}`}>
          {t.type === "success" && <Check size={15} className="text-[#22A65A] flex-shrink-0" />}
          {t.type === "warning" && <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />}
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   MODAL
───────────────────────────────────────────── */
function Modal({ open, onClose, children, maxW = "max-w-md" }) {
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-2xl shadow-2xl w-full ${maxW} border border-[#e8e0d0] animate-modal-in`}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, icon: Icon, onClose }) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b border-[#f0ebe0]">
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center">
          <Icon size={15} className="text-teal-700" />
        </div>
        <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
      </div>
      <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-all">
        <X size={16} />
      </button>
    </div>
  );
}

function ModalFooter({ onCancel, onConfirm, confirmLabel }) {
  return (
    <div className="flex gap-3 justify-end px-6 py-4 border-t border-[#f0ebe0]">
      <button onClick={onCancel}
        className="px-5 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-[#f7f3ec] transition-all active:scale-95">
        Cancel
      </button>
      <button onClick={onConfirm}
        className="px-5 py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium transition-all active:scale-95 flex items-center gap-2 shadow-sm">
        {confirmLabel}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SHARED FIELDS
───────────────────────────────────────────── */
function Field({ label, required, error, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function TextInput({ error, ...props }) {
  return (
    <input {...props}
      className={`w-full h-10 px-3 rounded-xl border text-sm bg-[#fdfcfa] text-gray-800 placeholder-gray-400 outline-none transition-all
        ${error ? "border-red-300 ring-2 ring-red-100"
          : "border-[#e2ddd4] hover:border-[#c5bfb5] focus:border-teal-500 focus:ring-2 focus:ring-teal-100"}`}
    />
  );
}

function StatusRow({ active, onChange }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-[#e2ddd4] bg-[#fdfcfa]">
      <div>
        <p className="text-sm font-medium text-gray-700">Status</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {active ? "This item is currently active" : "This item is currently inactive"}
        </p>
      </div>
      <button type="button" onClick={onChange}
        className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200
          ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}>
        <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200
          ${active ? "translate-x-5" : "translate-x-1"}`} />
      </button>
    </div>
  );
}

function StatusToggle({ active, onChange }) {
  return (
    <button type="button" onClick={onChange}
      className={`relative inline-flex items-center h-6 w-11 rounded-full border transition-all duration-200 flex-shrink-0
        ${active ? "bg-green-600 border-teal-700" : "bg-gray-200 border-gray-300"}`}>
      <span className={`inline-block w-4 h-4 rounded-full bg-white shadow-sm transform transition-transform duration-200
        ${active ? "translate-x-5" : "translate-x-1"}`} />
    </button>
  );
}

/* ─────────────────────────────────────────────
   ADD MODAL
───────────────────────────────────────────── */
function AddModal({ open, onClose, onSave }) {
  const [name, setName]     = useState("");
  const [active, setActive] = useState(true);
  const [error, setError]   = useState("");

  useEffect(() => { if (open) { setName(""); setActive(true); setError(""); } }, [open]);

  const handleSave = () => {
    if (!name.trim()) { setError("Style name is required"); return; }
    onSave({ name: name.trim(), active });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title="Add Style" icon={Plus} onClose={onClose} />
      <div className="px-6 py-5 space-y-4">
        <Field label="Style Name" required error={error}>
          <TextInput placeholder="e.g., Modern, Vintage, Abstract" value={name} autoFocus
            onChange={(e) => { setName(e.target.value); setError(""); }} error={error} />
        </Field>
        <StatusRow active={active} onChange={() => setActive((v) => !v)} />
      </div>
      <ModalFooter onCancel={onClose} onConfirm={handleSave}
        confirmLabel={<><Check size={14} /> Save Style</>} />
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   EDIT MODAL  — initialised from API item
───────────────────────────────────────────── */
function EditModal({ open, onClose, onSave, item }) {
  const [name, setName]     = useState("");
  const [active, setActive] = useState(true);
  const [error, setError]   = useState("");

  useEffect(() => {
    if (open && item) {
      setName(item.name || "");
      setActive(isActive(item));   // ← convert "Active"/"Inactive" → boolean
      setError("");
    }
  }, [open, item]);

  const handleSave = () => {
    if (!name.trim()) { setError("Name is required"); return; }
    onSave(item.id, { name: name.trim(), active });
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader title="Edit Style" icon={Pencil} onClose={onClose} />
      <div className="px-6 py-5 space-y-4">
        <Field label="Style Name" required error={error}>
          <TextInput placeholder="e.g., Modern, Vintage, Abstract" value={name} autoFocus
            onChange={(e) => { setName(e.target.value); setError(""); }} error={error} />
        </Field>
        <StatusRow active={active} onChange={() => setActive((v) => !v)} />
      </div>
      <ModalFooter onCancel={onClose} onConfirm={handleSave}
        confirmLabel={<><Check size={14} /> Update Style</>} />
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   DELETE MODAL
───────────────────────────────────────────── */
function DeleteModal({ open, onClose, onConfirm, item }) {
  return (
    <Modal open={open} onClose={onClose} maxW="max-w-sm">
      <div className="p-6 text-center">
        <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle size={22} className="text-red-500" />
        </div>
        <h3 className="text-base font-semibold text-gray-800 mb-1">Delete Style?</h3>
        <p className="text-sm text-gray-500 mb-6">
          Are you sure you want to delete <span className="font-semibold text-gray-700">"{item?.name}"</span>? This cannot be undone.
        </p>
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-[#e2ddd4] text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all active:scale-95">
            Cancel
          </button>
          <button onClick={() => { onConfirm(item?.id); onClose(); }}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all active:scale-95 flex items-center justify-center gap-2">
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ─────────────────────────────────────────────
   SKELETON ROW  (shown while loading)
───────────────────────────────────────────── */
function SkeletonRow() {
  return (
    <tr className="border-b border-[#f5f0e8]">
      <td className="px-5 py-4"><div className="h-4 w-6 bg-gray-100 rounded animate-pulse" /></td>
      <td className="px-5 py-4"><div className="h-4 w-32 bg-gray-100 rounded animate-pulse" /></td>
      <td className="px-5 py-4">
        <div className="flex items-center justify-end gap-3">
          <div className="h-6 w-11 bg-gray-100 rounded-full animate-pulse" />
          <div className="h-7 w-7 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-7 w-7 bg-gray-100 rounded-lg animate-pulse" />
        </div>
      </td>
    </tr>
  );
}

/* ─────────────────────────────────────────────
   STYLES PAGE
───────────────────────────────────────────── */
export default function StylesPage() {
  // ── context (replaces local useState(SEED)) ──────────────────
  const { shapes, shapeloading, addShape, updateShape, toggleStatus, deleteShape } =
    useProductShapes();

  const [search, setSearch]     = useState("");
  const [sortBy, setSortBy]     = useState("");
  const [addOpen, setAddOpen]   = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [delItem, setDelItem]   = useState(null);
  const { toasts, show: toast } = useToast();

  const activeCount   = shapes.filter(isActive).length;
  const inactiveCount = shapes.length - activeCount;
const [filtersOpen, setFiltersOpen] = useState(false);
const [statusFilter, setStatusFilter] = useState("all");
 let filtered = shapes.filter((i) => {
  const matchSearch = i.name.toLowerCase().includes(search.toLowerCase());

  const matchStatus =
    statusFilter === "all"
      ? true
      : statusFilter === "active"
      ? i.status === "Active"
      : i.status === "Inactive";

  return matchSearch && matchStatus;
});

  // Sort
  if (sortBy === "name_asc")  filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  if (sortBy === "name_desc") filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
if (sortBy === "active_first")
  filtered = [...filtered].sort((a, b) => {
    return (b.status === "Active") - (a.status === "Active");
  });
if (sortBy === "inactive_first")
  filtered = [...filtered].sort((a, b) => {
    return (a.status === "Active") - (b.status === "Active");
  });
  // ── handlers: bridge UI boolean → API status string ──────────
  const handleAdd = async ({ name, active }) => {
    await addShape({ name, status: active ? "Active" : "Inactive" });
    toast("Style added successfully");
  };

  const handleEdit = async (id, { name, active }) => {
    await updateShape(id, { name, status: active ? "Active" : "Inactive" });
    toast("Style updated successfully");
  };

  const handleDelete = async (id) => {
    await deleteShape(id);
    toast("Style deleted", "warning");
  };

  const handleToggle = async (item) => {
    await toggleStatus(item.id, item.status);   // context flips "Active"↔"Inactive"
  };

  return (
    <>
      <style>{`
        @keyframes modalIn { from { opacity:0; transform:scale(0.95) translateY(8px); } to { opacity:1; transform:scale(1) translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        .animate-modal-in { animation: modalIn 0.18s ease-out both; }
        .animate-slide-up { animation: slideUp 0.2s ease-out both; }
      `}</style>

      <div className="space-y-5">

        {/* ── Header ── */}
        <div className="rounded-2xl overflow-hidden">
          <div className="px-6 py-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-teal-700 flex items-center justify-center shadow-sm">
                  <Sparkles size={22} className="text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Styles</h1>
                  <p className="text-sm text-gray-500 mt-0.5">Manage your style categories</p>
                </div>
              </div>
              <button onClick={() => setAddOpen(true)}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl hover:bg-teal-700 transition-colors font-medium text-sm sm:text-base"
          ><Plus size={15} /> Add Style
              </button>
            </div>
          </div>
        </div>

        {/* ── Stat chips ── */}
        <div className="grid grid-cols-3 gap-3">
          <div className="px-6 py-4 bg-white rounded-2xl border border-[#e8e0d0]">
            <p className="text-xs text-gray-400 font-medium mb-1">Total</p>
            <p className="text-2xl font-bold text-gray-800">{shapes.length}</p>
          </div>
          <div className="px-6 py-4 bg-white rounded-2xl border border-[#e8e0d0]">
            <p className="text-xs text-gray-400 font-medium mb-1">Active</p>
            <p className="text-2xl font-bold text-teal-600">{activeCount}</p>
          </div>
          <div className="px-6 py-4 bg-white rounded-2xl border border-[#e8e0d0]">
            <p className="text-xs text-gray-400 font-medium mb-1">Inactive</p>
            <p className="text-2xl font-bold text-red-500">{inactiveCount}</p>
          </div>
        </div>

        {/* ── Search + Sort ── */}
        <div className="rounded-xl border border-orange-300 overflow-hidden">
         {shapes.length > 0 && (
  <div className="rounded-2xl border border-[#e8e0d0] overflow-hidden bg-white">

    {/* Search row */}
    <div className="px-5 py-4 flex items-center gap-3">
      <div className="relative flex-1">
        <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />

        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search style…"
          className="w-full pl-9 pr-9 h-14 rounded-xl border border-[#e2ddd4] text-sm bg-[#fdfcfa] text-gray-700 placeholder-gray-400 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all"
        />

        {search && (
          <button
            onClick={() => setSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Filter button */}
      <button
        onClick={() => setFiltersOpen((v) => !v)}
        className={`inline-flex items-center gap-2 px-8 h-14 rounded-xl border text-sm font-medium transition-all
          ${filtersOpen
            ? "bg-teal-50 border-teal-300 text-teal-700"
            : "bg-white border-[#e2ddd4] text-gray-600 hover:border-teal-300 hover:text-teal-700"}`}
      >
        <Filter size={14} />
        Filters
        <svg
          width="12" height="12" viewBox="0 0 12 12" fill="none"
          className={`transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}
        >
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>

    {/* Expandable filter panel */}
    {filtersOpen && (
      <div className="px-5 pb-4 pt-0 border-t border-[#f0ebe0] bg-[#fffcf9]">
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
{/* Status Filter */}
<div className="flex flex-col gap-1.5">
  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
    Status
  </label>

  <select
    value={statusFilter}
    onChange={(e) => setStatusFilter(e.target.value)}
    className="h-10 px-3 rounded-xl border border-[#e2ddd4] text-sm bg-white text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all cursor-pointer"
  >
    <option value="all">All Styles ({shapes.length})</option>
    <option value="active">Active ({activeCount})</option>
    <option value="inactive">Inactive ({inactiveCount})</option>
  </select>
</div>
          {/* Sort */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-10 px-3 rounded-xl border border-[#e2ddd4] text-sm bg-white text-gray-700 outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-100 transition-all cursor-pointer"
            >
              <option value="">Default</option>
              <option value="name_asc">Name A–Z</option>
              <option value="name_desc">Name Z–A</option>
              <option value="active_first">Active First</option>
<option value="inactive_first">Inactive First</option>
            </select>
          </div>

        </div>
      </div>
    )}
  </div>
)}
        </div>

        {/* ── Table card ── */}
        <div className="bg-white rounded-2xl border border-[#e8e0d0] shadow-sm overflow-hidden">
          {!shapeloading && shapes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center px-4">
              <div className="w-14 h-14 rounded-2xl bg-teal-50 border border-teal-200 flex items-center justify-center mb-4">
                <Sparkles size={26} className="text-teal-400" />
              </div>
              <p className="text-sm font-semibold text-gray-600 mb-1">No styles yet</p>
              <p className="text-xs text-gray-400">Click "+ Add Style" above to get started</p>
            </div>
          ) : !shapeloading && filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center px-4">
              <Search size={22} className="text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No results for "<span className="font-medium">{search}</span>"</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#f0ebe0] bg-[#fdfcfa]">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 w-16">ID</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500">Style</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f5f0e8]">
                  {shapeloading
                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
                    : filtered.map((item, idx) => (
                        <tr key={item.id} className="hover:bg-[#fdfcfa] transition-colors group">
                          <td className="px-5 py-4 text-sm text-gray-700">{idx + 1}</td>
                          <td className="px-5 py-4 text-sm font-medium text-gray-800">{item.name}</td>
                          <td className="px-5 py-4">
                            <div className="flex items-center justify-end gap-3">
                              <StatusToggle
                                active={isActive(item)}          // ← "Active" → true
                                onChange={() => handleToggle(item)}
                              />
                              <button onClick={() => setEditItem(item)}
                                className="p-1.5 rounded-lg text-teal-700 transition-all active:scale-95">
                                <Edit3 size={14} />
                              </button>
                              <button onClick={() => setDelItem(item)}
                                className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-all active:scale-95">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                  }
                </tbody>
              </table>
            </div>
          )}

          {shapes.length > 0 && (
            <div className="px-5 py-3 border-t border-[#f5f0e8] bg-[#fdfcfa] flex items-center justify-between">
              <span className="text-[11px] text-gray-400">Showing {filtered.length} of {shapes.length} styles</span>
              {search && filtered.length < shapes.length && (
                <button onClick={() => setSearch("")} className="text-[11px] text-teal-600 hover:underline">Clear filter</button>
              )}
            </div>
          )}
        </div>
      </div>

      <AddModal open={addOpen} onClose={() => setAddOpen(false)} onSave={handleAdd} />
      <EditModal open={!!editItem} onClose={() => setEditItem(null)} onSave={handleEdit} item={editItem} />
      <DeleteModal open={!!delItem} onClose={() => setDelItem(null)} onConfirm={handleDelete} item={delItem} />
      <ToastContainer toasts={toasts} />
    </>
  );
}